import { Box, Flex, Text } from '@pancakeswap/uikit'
import Link from 'next/link'
import { useEffect, useMemo, useRef, useState } from 'react'
import styled, { css, keyframes } from 'styled-components'
import useSWR from 'swr'
import { EXPLORER, fetcher } from 'views/TradingCompetitionV3/config'
import { useV3Wallet } from 'views/TradingCompetitionV3/hooks'
import type { PoolView } from 'views/TradingCompetitionV3/pool-search/logic'
import { checksum, shortChecksum } from 'views/TradingCompetitionV3/identity/logic'
import { useProfiles } from 'views/TradingCompetitionV3/identity/useProfiles'
import { ALL_POOLS } from '../config'
import { fmtAgo, fmtAmount, fmtUsd } from '../format'
import { Icon } from '../icons'
import { GhostCta, MONO, Num, Panel, SectionTitle, SegBtn, Segmented, Skel, useNow } from '../ui'
import { poolOptionLabel } from './PoolPicker'
import WolfState from './WolfState'
import type { Address, Trade, TradesResponse } from '../types'

/** A small deterministic avatar for a wallet: a 5 x 5 mirrored pattern in a colour taken from the address. No image, no request. */
export function Identicon({ address, size = 22 }: { address: string; size?: number }) {
  const hex = address.toLowerCase().replace(/^0x/, '').padEnd(40, '0')
  const n = (i: number) => parseInt(hex.slice(i, i + 2), 16)
  const hue = (n(0) * 360) / 256
  const cells: Array<[number, number]> = []
  for (let y = 0; y < 5; y += 1) {
    for (let x = 0; x < 3; x += 1) {
      if (n(2 + y * 3 + x) % 2 === 0) {
        cells.push([x, y])
        if (x < 2) cells.push([4 - x, y])
      }
    }
  }
  return (
    <svg width={size} height={size} viewBox="0 0 5 5" aria-hidden="true" focusable="false" style={{ flex: 'none', borderRadius: 6, background: `hsl(${hue} 40% 18%)` }} shapeRendering="crispEdges">
      {cells.map(([x, y]) => (
        <rect key={`${x}-${y}`} x={x} y={y} width="1" height="1" fill={`hsl(${hue} 75% 62%)`} />
      ))}
    </svg>
  )
}

const flash = keyframes`
  0% { background: rgba(127, 212, 232, 0.28); }
  100% { background: transparent; }
`
const Row = styled.div<{ $fresh?: boolean; $head?: boolean }>`
  display: grid;
  align-items: center;
  gap: 4px 12px;
  min-width: 0;
  padding: 10px 6px;
  border-top: ${({ $head, theme }) => ($head ? '0' : `1px solid ${theme.colors.cardBorder}`)};
  font-size: ${({ $head }) => ($head ? 12 : 14)}px;
  color: ${({ $head, theme }) => ($head ? theme.colors.textSubtle : theme.colors.text)};
  /* phone: two stacked lines per trade, nothing scrolls sideways */
  grid-template-columns: auto minmax(0, 1fr) auto;
  grid-template-areas:
    'side amount time'
    'who  paid   tx';
  .side { grid-area: side; }
  .amount { grid-area: amount; min-width: 0; }
  .time { grid-area: time; text-align: right; }
  .who { grid-area: who; min-width: 0; }
  .paid { grid-area: paid; min-width: 0; color: ${({ theme }) => theme.colors.textSubtle}; font-size: 13px; }
  .tx { grid-area: tx; justify-self: end; }
  .usd, .quote { display: none; }
  ${({ $head }) => ($head ? 'display: none;' : '')}
  ${({ $fresh }) =>
    $fresh &&
    css`
      animation: ${flash} 2.4s ease-out 1;
      @media (prefers-reduced-motion: reduce) {
        animation: none;
        background: rgba(127, 212, 232, 0.1);
      }
    `}
  ${({ theme }) => theme.mediaQueries.md} {
    grid-template-columns: minmax(150px, 1.25fr) 64px minmax(0, 1fr) minmax(0, 1fr) minmax(0, 0.8fr) 72px 44px;
    grid-template-areas: 'who side amount quote usd time tx';
    ${({ $head }) => ($head ? 'display: grid;' : '')}
    min-height: ${({ $head }) => ($head ? 32 : 52)}px;
    .paid { display: none; }
    .quote, .usd { display: block; text-align: right; }
    .amount { text-align: right; }
    .time { text-align: right; }
    .tx { justify-self: center; }
  }
`
const SidePill = styled.span<{ $buy: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 48px;
  min-height: 26px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  color: ${({ theme, $buy }) => ($buy ? theme.colors.success : theme.colors.failure)};
  background: ${({ $buy }) => ($buy ? 'rgba(57, 217, 138, 0.14)' : 'rgba(255, 107, 122, 0.14)')};
`
const Counts = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 6px;
  font-size: 12px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.success};
`
const TxLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  margin: -8px;
  color: ${({ theme }) => theme.colors.textSubtle};
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: -4px;
    border-radius: 12px;
  }
`
const WalletLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  min-height: 44px;
  margin: -11px 0;
  color: inherit;
  font-family: ${MONO};
  font-size: 13px;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`
const Select = styled.select`
  min-height: 44px;
  max-width: 100%;
  padding: 0 12px;
  border-radius: 14px;
  font-family: inherit;
  font-weight: 700;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text};
  background: rgba(5, 7, 19, 0.6);
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
`
const Toolbar = styled.div`
  display: grid;
  gap: 8px;
  margin: 12px 0;
  grid-template-columns: minmax(0, 1fr);
  ${({ theme }) => theme.mediaQueries.md} {
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
  }
`
const Check = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  font-size: 14px;
  cursor: pointer;
  input {
    width: 20px;
    height: 20px;
  }
`

const STEP = 40

export interface TradeHistoryProps {
  token: Address
  symbol: string
  /** token page: the pools to filter by (strongest first); the default view merges the strongest few */
  pools?: PoolView[]
  /** competition page: read this pool only */
  fixedPool?: Address
  /** competition page: the window that counts; by default only trades inside it are listed */
  window?: { start: number; end: number }
  /** an ack-free way to tell the page a new trade arrived (charts refresh) */
  onNew?: () => void
  title?: string
}

/**
 * Trade history under a chart: newest first, live (refreshed every 15 s while the tab is visible), new trades flash once, a filter for the pool
 * (token page) or for the competition window (competition page), "My trades" when a wallet is connected, and "Load more". The account is the
 * transaction's sender, the wallet a competition credits. Stacked rows on a phone.
 */
export default function TradeHistory({ token, symbol, pools, fixedPool, window: win, onNew, title = 'Trade history' }: TradeHistoryProps) {
  const [poolSel, setPoolSel] = useState<string>('all')
  const [limit, setLimit] = useState(STEP)
  const [mine, setMine] = useState(false)
  const [inWindow, setInWindow] = useState(true)
  const { address, isConnected } = useV3Wallet()
  const now = useNow(15_000)

  const pool = fixedPool ?? poolSel
  const bounds = win && inWindow ? { since: win.start, until: win.end } : null
  const url = `/api/tokens/${token}/trades?pool=${pool}&limit=${limit}${bounds ? `&since=${bounds.since}&until=${bounds.until}` : ''}`
  const { data, error, isLoading, isValidating } = useSWR<TradesResponse>(token ? url : null, fetcher, {
    refreshInterval: 15_000,
    keepPreviousData: true,
    revalidateOnFocus: true,
    shouldRetryOnError: false,
    dedupingInterval: 4_000,
  })

  // new trades flash once; older ones that "Load more" adds do not
  const seen = useRef<{ ids: Set<string>; newest: number } | null>(null)
  const [fresh, setFresh] = useState<Set<string>>(new Set())
  useEffect(() => {
    if (!data) return undefined
    const ids = data.trades.map((t) => t.id)
    if (seen.current === null) {
      seen.current = { ids: new Set(ids), newest: Math.max(0, ...data.trades.map((t) => t.t)) }
      return undefined
    }
    const { ids: prev, newest } = seen.current
    const added = data.trades.filter((t) => !prev.has(t.id) && t.t >= newest).map((t) => t.id)
    data.trades.forEach((t) => prev.add(t.id))
    seen.current.newest = Math.max(newest, ...data.trades.map((t) => t.t))
    if (!added.length) return undefined
    setFresh(new Set(added))
    onNew?.()
    const timer = setTimeout(() => setFresh(new Set()), 2600)
    return () => clearTimeout(timer)
  }, [data, onNew])
  // a different view is a different list: nothing flashes on its first load
  useEffect(() => {
    seen.current = null
    setFresh(new Set())
  }, [pool, inWindow])

  // the /u handle of each trader on screen: one batched lookup for the whole list (no per-row request)
  const profiles = useProfiles(useMemo(() => Array.from(new Set((data?.trades ?? []).map((t) => t.wallet).filter((w): w is string => Boolean(w)))), [data]))
  const me = address?.toLowerCase()
  const trades: Trade[] = useMemo(() => {
    const all = data?.trades ?? []
    return mine && me ? all.filter((t) => t.wallet === me) : all
  }, [data, mine, me])
  const counts = (t: Trade) => Boolean(win) && t.t >= (win?.start ?? 0) && t.t <= (win?.end ?? 0)
  const partial = Boolean(win && inWindow && data?.coverageFrom && win.start < data.coverageFrom)
  const loadingMore = isValidating && Boolean(data) && (data?.trades.length ?? 0) < limit && limit > STEP

  return (
    <Panel data-testid="th-panel" aria-label={title}>
      <Flex alignItems="center" justifyContent="space-between" flexWrap="wrap" style={{ gap: 8 }}>
        <SectionTitle style={{ fontSize: 20 }}>{title}</SectionTitle>
        <Text fontSize="12px" color="textSubtle" data-testid="th-live">Live · refreshes every 15 s</Text>
      </Flex>

      <Toolbar>
        <Flex style={{ gap: 12 }} flexWrap="wrap" alignItems="center">
          {pools && pools.length > 0 && !fixedPool && (
            <Select value={poolSel} onChange={(e) => { setPoolSel(e.target.value); setLimit(STEP) }} aria-label="Pool filter" data-testid="th-pool-filter">
              <option value="all">All pools (the strongest {Math.min(ALL_POOLS, pools.length)})</option>
              {pools.map((p) => (
                <option key={p.pool} value={p.pool}>{poolOptionLabel(p)}</option>
              ))}
            </Select>
          )}
          {isConnected && (
            <Check htmlFor="th-mine">
              <input id="th-mine" type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} data-testid="th-mine" />
              My trades
            </Check>
          )}
        </Flex>
        {win && (
          <Segmented $cols={2} role="tablist" aria-label="Which trades" style={{ minWidth: 0 }}>
            <SegBtn role="tab" aria-selected={inWindow} $on={inWindow} onClick={() => { setInWindow(true); setLimit(STEP) }} data-testid="th-window">In the competition</SegBtn>
            <SegBtn role="tab" aria-selected={!inWindow} $on={!inWindow} onClick={() => { setInWindow(false); setLimit(STEP) }} data-testid="th-all">All trades</SegBtn>
          </Segmented>
        )}
      </Toolbar>

      {isLoading && !data && <Box aria-busy="true">{[0, 1, 2, 3].map((i) => <Skel key={i} $h={44} style={{ marginBottom: 8 }} />)}</Box>}
      {error && !data && <Text color="failure" role="alert" data-testid="th-error">Trades could not be loaded right now.</Text>}

      {data && trades.length === 0 && (
        <WolfState
          compact
          testId="th-empty"
          title={mine ? 'None of your trades here' : win && inWindow ? 'No trades in the competition window yet' : 'No trades yet'}
          body={mine ? 'Only the trades loaded so far are checked. Load more, or switch off My trades.' : data.source === 'none' ? 'This pool has no trade history we can read.' : win && inWindow ? 'Trades through this pool between the start and the end appear here.' : 'Nothing has traded in this view recently.'}
        />
      )}

      {data && trades.length > 0 && (
        <Box role="table" aria-label={`${symbol} trades`} data-testid="th-table" data-source={data.source}>
          <Row $head role="row">
            <span role="columnheader">Account</span>
            <span role="columnheader">Side</span>
            <span role="columnheader" style={{ textAlign: 'right' }}>{symbol}</span>
            <span role="columnheader" style={{ textAlign: 'right' }}>Paid or received</span>
            <span role="columnheader" style={{ textAlign: 'right' }}>USD</span>
            <span role="columnheader" style={{ textAlign: 'right' }}>When</span>
            <span role="columnheader" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden' }}>Transaction</span>
          </Row>
          {trades.map((t) => (
            <Row key={t.id} role="row" $fresh={fresh.has(t.id)} data-side={t.side} data-fresh={fresh.has(t.id) ? 'true' : undefined} data-counts={win ? String(counts(t)) : undefined} data-testid="th-row">
              <span className="who" role="cell">
                {t.wallet ? (
                  <WalletLink href={`/u/${checksum(t.wallet)}`} title={`${profiles[t.wallet]?.name ? `${profiles[t.wallet]?.name} · ` : ''}${checksum(t.wallet)}: open CADINU profile`} data-testid="th-who">
                    <Identicon address={t.wallet} />
                    {profiles[t.wallet]?.state === 'on' && profiles[t.wallet]?.name ? <span style={{ fontFamily: 'inherit', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis' }}>{profiles[t.wallet]?.name}</span> : shortChecksum(t.wallet)}
                  </WalletLink>
                ) : (
                  <Text color="textSubtle">—</Text>
                )}
              </span>
              <span className="side" role="cell">
                <SidePill $buy={t.side === 'buy'}>{t.side === 'buy' ? 'Buy' : 'Sell'}</SidePill>
                {counts(t) && <Counts title="Inside the competition window on this pool: it counts" data-testid="th-counts"><Icon name="check" size={12} strokeWidth={3} />counts</Counts>}
              </span>
              <span className="amount" role="cell">
                <Num>{fmtAmount(t.tokenAmount)}</Num> <span style={{ color: '#8f9bb8', fontSize: 12 }}>{symbol}</span>
              </span>
              <span className="paid" role="cell">
                {t.side === 'buy' ? 'paid' : 'got'} {t.quoteAmount !== null ? fmtAmount(t.quoteAmount) : '—'} {t.quoteSymbol}{t.usd !== null ? ` · ${fmtUsd(t.usd)}` : ''}
              </span>
              <span className="quote" role="cell">
                <Num>{t.quoteAmount !== null ? fmtAmount(t.quoteAmount) : '—'}</Num> <span style={{ color: '#8f9bb8', fontSize: 12 }}>{t.quoteSymbol}</span>
              </span>
              <span className="usd" role="cell">
                <Num>{fmtUsd(t.usd)}</Num>
              </span>
              <span className="time" role="cell" style={{ color: '#8f9bb8', fontSize: 13 }} title={new Date(t.t * 1000).toISOString()}>
                {now === null ? '' : fmtAgo(t.t, now)}
              </span>
              <span className="tx" role="cell">
                {t.tx && (
                  <TxLink href={`${EXPLORER}/tx/${t.tx}`} target="_blank" rel="noopener noreferrer" aria-label="Open transaction on BscScan">
                    <Icon name="external" size={18} />
                  </TxLink>
                )}
              </span>
            </Row>
          ))}
        </Box>
      )}

      {data && partial && (
        <Text fontSize="12px" color="textSubtle" mt="8px" data-testid="th-partial">
          {data.source === 'cadinudex'
            ? 'The older history of this pool is still being loaded. Trades before '
            : 'This exchange\u2019s history comes from GeckoTerminal, which keeps 24 hours. Trades before '}
          {new Date((data.coverageFrom ?? 0) * 1000).toISOString().slice(0, 16).replace('T', ' ')} UTC are not listed here{data.source === 'cadinudex' ? ' yet' : ''}.
        </Text>
      )}
      {data?.more && limit < 300 && !mine && (
        <GhostCta as="button" type="button" onClick={() => setLimit((l) => Math.min(300, l + STEP))} disabled={loadingMore} style={{ minHeight: 44, width: '100%', marginTop: 12 }} data-testid="th-more">
          {loadingMore ? 'Loading…' : 'Load more'}
        </GhostCta>
      )}
      {win && (
        <Text fontSize="12px" color="textSubtle" mt="12px">
          <b>counts</b> marks a trade through this pool between the start and the end, credited to the wallet that sent the transaction.
        </Text>
      )}
    </Panel>
  )
}
